/**
 * AnimatedSection — scroll-triggered Framer Motion wrapper
 *
 * Wraps any content block and fades + slides it in when it enters
 * the viewport. Uses `whileInView` with `once: true` so the
 * animation only fires the first time the element scrolls into view.
 *
 * Props:
 *   children — content to animate
 *   className — optional extra classes
 *   delay — entrance delay in seconds (default 0)
 */

"use client";

import { type ReactNode } from "react";
import { motion, type Variants } from "framer-motion";

interface AnimatedSectionProps {
    children: ReactNode;
    className?: string;
    /** Entrance delay in seconds */
    delay?: number;
}

const variants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
};

export function AnimatedSection({
    children,
    className,
    delay = 0,
}: AnimatedSectionProps) {
    return (
        <motion.div
            variants={variants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            transition={{
                duration: 0.6,
                delay,
                ease: "easeOut",
            }}
            className={className}
        >
            {children}
        </motion.div>
    );
}
